import { TranslateService } from '@project/translate';
import { CsvParserService } from '@core/services';
import { FormBuilder } from '@angular/forms';
import { NotificationsService } from '@lib/notifications';
import { map, takeUntil } from 'rxjs/operators';
import { EGenderDTO } from '@project/view-models';
import { FormHelpers, IRowValidationResult } from '@core/helpers';
import { IPatientInviteFormModel, PatientInviteUtils } from '../patient-invite.common';
import { EPatientsModuleAction, PatientInviteManagerService } from '../patient-invite-manager.service';
import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy } from '@angular/core';
import { IModalComponent } from '@lib/modal';
import { BehaviorSubject, Subject } from 'rxjs';
import { HcFeature, HcRule, HcService } from '@project/services';
import { HCConfigFeatures, HCConfigRules } from 'src/hc-config/hc-config';
import { CsvField } from './types/csv-field';

@Component({
  selector: 'app-patient-invite-import-modal',
  templateUrl: 'patient-invite-import-modal.component.html',
  styleUrls: ['./patient-invite-import-modal.component.scss'],
})
export class PatientInviteImportModalComponent implements IModalComponent, OnDestroy {
  close$ = new EventEmitter<void>();
  submit$ = new EventEmitter();

  private readonly destroy$ = new Subject();
  private readonly tagFilter$ = this.patientInviteManagerService.tagFilter$;
  private readonly csvFields: CsvField[] = [
    { label: 'First name', key: 'firstName', value: 'Alex', defaultRequired: true },
    { label: 'Last name', key: 'lastName', value: 'Standson', defaultRequired: true },
    { label: 'Email', key: 'email', value: 'emailOfPatient4@pat.com', defaultRequired: true },
    { label: 'Gender (Male/Female)', key: 'gender', value: 'Male', defaultRequired: true },
    { label: 'Phone', key: 'phone', value: '11 99238-2929', defaultRequired: true },
    { label: 'Date of birth (19.12.1990)', key: 'dateOfBirth', value: '14.11.1994', defaultRequired: true },
    { label: 'CPF', key: 'cpf', value: '092.443.190-39', defaultRequired: true },
    { label: 'CNS', key: 'cns', value: '199621022540009', defaultRequired: false },
    { label: 'Matricula Externa', key: 'externalId', value: '12345', defaultRequired: false },
  ];

  public readonly columnLabelsMap: { [K in keyof IPatientInviteFormModel]: string } = {
    firstName: TranslateService.localize('nouns.first-name'),
    lastName: TranslateService.localize('nouns.last-name'),
    email: TranslateService.localize('nouns.email'),
    gender: TranslateService.localize('nouns.gender'),
    phone: TranslateService.localize('nouns.phone'),
    dateOfBirth: TranslateService.localize('nouns.date-of-birth'),
    tag: TranslateService.localize('nouns.group'),
    cpf: TranslateService.localize('nouns.cpf-number'),
    cns: TranslateService.localize('nouns.cns'),
    externalId: TranslateService.localize('nouns.external-id'),
  };

  // Live reads (not cached fields): unlike the other patient forms, this modal has no persistent
  // reactive form to desync - the validation FormGroup is rebuilt from scratch every time a file
  // is selected (see onSelectFile), so there's nothing to preserve.
  @HcFeature(HCConfigFeatures.PATIENT_INVITE_AND_EDIT_FORM_INCLUDE_CNS)
  public readonly hcHasCns: boolean;

  @HcFeature(HCConfigFeatures.PATIENT_INVITE_AND_EDIT_FORM_INCLUDE_EXTERNAL_ID)
  public readonly hcHasExternalId: boolean;

  @HcRule(HCConfigRules.PATIENT_INVITE_AND_EDIT_FORM_REQUIRE_EMAIL)
  public readonly hcRequireEmail: boolean;

  @HcRule(HCConfigRules.PATIENT_INVITE_AND_EDIT_FORM_REQUIRE_CNS)
  public readonly hcRequireCns: boolean;

  @HcRule(HCConfigRules.PATIENT_INVITE_AND_EDIT_FORM_REQUIRE_CPF)
  public readonly hcRequireCpf: boolean;

  @HcRule(HCConfigRules.PATIENT_INVITE_AND_EDIT_FORM_REQUIRE_CPF_OR_EMAIL)
  public readonly hcRequireCpfOrEmail: boolean;

  @HcRule(HCConfigRules.PATIENT_INVITE_AND_EDIT_FORM_REQUIRE_EXTERNAL_ID)
  public readonly hcRequireExternalId: boolean;

  private _validItems$ = new BehaviorSubject<IRowValidationResult<IPatientInviteFormModel>[]>([]);
  private _invalidItems$ = new BehaviorSubject<IRowValidationResult<IPatientInviteFormModel>[]>([]);

  public validItems$ = this._validItems$.asObservable();
  public invalidItems$ = this._invalidItems$.asObservable();

  constructor(
    private csvParserService: CsvParserService,
    private formBuilder: FormBuilder,
    private notificationsService: NotificationsService,
    private changeDetectorRef: ChangeDetectorRef,
    private patientInviteManagerService: PatientInviteManagerService,
    private hcService: HcService,
  ) {}

  ngOnDestroy() {
    this.destroy$.next();
    this.patientInviteManagerService.dispatchAction(EPatientsModuleAction.ClearTagSelected);
  }

  close() {
    this.close$.emit();
  }

  doSuccess() {
    this.submit$.next(
      this._validItems$.value.map((records) => PatientInviteUtils.createPatientInviteDto(records.record)),
    );
  }

  public downloadCSV() {
    const csvFields = this.getCsvFields();
    const separator = ';';
    const header = csvFields.map((f) => f.label).join(separator);
    const row = csvFields.map((f) => f.value ?? '').join(separator);
    const csvContent = header + '\r\n' + row;

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `patients-invite-template.csv`);
    link.click();
    URL.revokeObjectURL(url);
  }

  public onSelectFile(event: Event) {
    this._invalidItems$.next([]);
    this._validItems$.next([]);

    this.patientInviteManagerService.dispatchAction(EPatientsModuleAction.SetTagSelected);

    const fileInput = event.target as HTMLInputElement;
    const file = fileInput.files[0];
    if (!file) return;

    const fieldsToParse = this.getCsvFields().map((f) => f.key);
    this.csvParserService
      .parseFile(file, fieldsToParse)
      .pipe(
        map((records) => this.mapCSVToFormModel(records)),
        map((records) =>
          FormHelpers.validateRecords(
            records,
            this.formBuilder.group(
              PatientInviteUtils.getPatientImportFormDeclaration(
                this.getSkippedFieldsByHc(),
                this.getRequiredFieldsByHc(),
              ),
            ),
          ),
        ),
        takeUntil(this.destroy$),
      )
      .subscribe(
        (validatedResult: IRowValidationResult<IPatientInviteFormModel>[]) => {
          let invalid: IRowValidationResult<IPatientInviteFormModel>[] = [];
          let valid: IRowValidationResult<IPatientInviteFormModel>[] = [];

          invalid = validatedResult.filter((validatedRecord) => !validatedRecord.isValid);
          valid = validatedResult
            .filter((validatedRecord) => validatedRecord.isValid)
            .map((validatedRecord) => validatedRecord);

          const res = [];
          validatedResult.map((item: IRowValidationResult<IPatientInviteFormModel>) => {
            const existItem = res.find((x) => x.record.email === item.record.email);
            if (existItem) {
              invalid.push(item);
            }
            res.push(item);
          }, []);

          invalid = invalid.filter((v, i, a) => a.findIndex((t) => t.record.email === v.record.email) === i);

          valid = valid.filter((v) => invalid.find((r) => r) !== v);

          this._validItems$.next(valid);
          this._invalidItems$.next(invalid);
        },
        (err) => {
          console.warn(err);
          this.notificationsService.error({
            message: TranslateService.localize('import-invites.errors.parse-file-error'),
          });

          fileInput.value = null;
          this.changeDetectorRef.markForCheck();
        },
      );
  }

  private mapCSVToFormModel(records: { [x: string]: string }[]): IPatientInviteFormModel[] {
    const mappedRecords = records.map(
      (record): IPatientInviteFormModel => {
        const mappedRecord: IPatientInviteFormModel = {
          ...record,
          firstName: record.firstName.trim(),
          lastName: record.lastName.trim(),
          phone: record.phone.trim(),
          email: record.email.trim(),
          dateOfBirth: record.dateOfBirth.trim().replace(/[/]/g, '.'),
          gender: EGenderDTO[record.gender.trim()] ?? record.gender.trim(),
          tag: this.tagFilter$.value ?? '00000000-0000-0000-0000-000000000000',
          cpf: record.cpf ? record.cpf.trim() : null,
          cns: record.cns ? record.cns.trim() : null,
          externalId: record.externalId ? record.externalId.trim() : null,
        };

        return mappedRecord;
      },
    );
    return mappedRecords;
  }

  private getCsvFields() {
    const skippedFields = [...this.getSkippedFieldsByHc()];
    const requiredFields = [...this.getRequiredFieldsByHc()];

    let csvFields = JSON.parse(JSON.stringify(this.csvFields)) as CsvField[];

    csvFields = csvFields.filter((field) => !skippedFields.includes(field.key));

    csvFields = csvFields.map((field) => {
      const isRequired = requiredFields.includes(field.key) || field.defaultRequired;
      return { ...field, label: isRequired ? `${field.label} *` : field.label };
    });

    return csvFields;
  }

  private getSkippedFieldsByHc(): string[] {
    const skippedFields = [];

    if (!this.hcHasCns) skippedFields.push('cns');
    if (!this.hcHasExternalId) skippedFields.push('externalId');

    return skippedFields;
  }

  private getRequiredFieldsByHc(): string[] {
    const requiredFields = [];

    if (this.hcRequireCpf) requiredFields.push('cpf');
    if (this.hcRequireExternalId) requiredFields.push('externalId');
    if (this.hcRequireCns) requiredFields.push('cns');
    if (this.hcRequireEmail) requiredFields.push('email');

    return requiredFields;
  }
}
